import { Stack } from '@fluentui/react';
import { useStyles } from './index.styles';
import React from 'react';
import {
  invoiceSigningAvailableTags,
  invoiceSigningAvailableTagsVariables,
} from 'common/graphql/__generated__/InvoiceSignAvailTags';
import {
  GetInvoiceTags,
  GetInvoiceTagsVariables,
} from 'common/graphql/__generated__/GetInvoiceTags';
import {
  EntityTagInvoiceSignCreate,
  EntityTagInvoiceSignCreateVariables,
} from 'common/graphql/__generated__/EntityTagInvoiceSignCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';
import { EntityTagInput } from 'common/types/globalTypes';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';

import INVOICE_TAGS_LIST from 'common/graphql/GetInvoiceTags.graphql';
import AVAILABLE_TAGS_LIST from 'common/graphql/InvoiceSignAvailTags.graphql';
import ADD_NEW_TAG from 'common/graphql/EntityTagInvoiceSignCreate.graphql';
import DELETE_TAG from 'common/graphql/EntityTagDelete.graphql';

interface TagsSectionProps {
  invoiceId?: string;
}

export const TagsSection: React.FC<TagsSectionProps> = ({ invoiceId }) => {
  const styles = useStyles();

  const extractAvailableTags = (
    data: invoiceSigningAvailableTags | undefined
  ) => {
    return (
      data?.invoiceSigningAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? []
    );
  };

  const extractEntityTags = (data: GetInvoiceTags | undefined) => {
    return (
      data?.invoice?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown', // Handle null
      })) ?? []
    );
  };

  const getAvailableTagsVars = (
    entityId: string
  ): invoiceSigningAvailableTagsVariables => ({
    id: entityId,
  });

  const getEntityTagsVars = (entityId: string): GetInvoiceTagsVariables => ({
    id: entityId,
  });

  const createTagsVars = (
    tags: EntityTagInput[]
  ): EntityTagInvoiceSignCreateVariables => ({
    input: {
      entityTag: tags,
    },
  });

  const deleteTagsVars = (
    id: string,
    rowTimestamp: string
  ): EntityTagDeleteVariables => ({
    entityTagDeleteInput: {
      entityDelete: [{ id, rowTimestamp }],
    },
  });

  return (
    <Stack tokens={{ childrenGap: 15 }} className={styles.tagsContainer}>
      {invoiceId && (
        <EntityTagsSection<
          invoiceSigningAvailableTags,
          invoiceSigningAvailableTagsVariables,
          GetInvoiceTags,
          GetInvoiceTagsVariables,
          EntityTagInvoiceSignCreate,
          EntityTagInvoiceSignCreateVariables,
          EntityTagDelete,
          EntityTagDeleteVariables
        >
          entityId={invoiceId}
          getAvailableTagsGql={AVAILABLE_TAGS_LIST}
          getEntityTagsGql={INVOICE_TAGS_LIST}
          addTagGql={ADD_NEW_TAG}
          deleteTagGql={DELETE_TAG}
          extractAvailableTags={extractAvailableTags}
          extractEntityTags={extractEntityTags}
          getAvailableTagsVars={getAvailableTagsVars}
          getEntityTagsVars={getEntityTagsVars}
          createTagsVars={createTagsVars}
          deleteTagsVars={deleteTagsVars}
        />
      )}
    </Stack>
  );
};
